import { useQuery } from 'react-query';
import { predictionManagerAxiosApi } from 'src/Config/axiosApi';

/**
 * The `venue_accounts` registry, served by prediction-manager (`GET /accounts`),
 * which owns the CRUD. The endpoint is `@InternalOnly()`, which is ADDITIVE in
 * this codebase — internal callers skip the JWT, browsers still authenticate —
 * so the standard axios instance needs no special handling.
 *
 * The registry NEVER carries credential material: an account's api secrets live
 * only in the k8s Secret named by `secretName`.
 */
export type VenuePlatform = 'kalshi' | 'polymarket';

export interface VenueAccount {
  /** Operator-chosen slug, unique PER PLATFORM — never across. */
  accountId: string;
  platform: VenuePlatform;
  /** Display name. Every label, badge and tooltip renders this, not the id. */
  name: string;
  /** Only an enabled account may back a running group. */
  enabled: boolean;
  /** Capacity budget in armed maker leg-units; `null` = uncapped. */
  maxQuotedMarkets: number | null;
  secretName?: string;
  notes?: string;
}

export const VENUE_ACCOUNTS_KEY = ['venue-accounts'];

/**
 * Unwrap the bare-array response, THROWING on any other shape.
 *
 * A non-array body (prediction-manager not deployed yet, a proxy error page, an
 * auth redirect) would otherwise leave the query SUCCESSFUL with no rows, and
 * every account select would render empty — the "this build has no accounts"
 * misread this exists to prevent.
 */
export function parseVenueAccountsResponse(data: unknown): VenueAccount[] {
  if (!Array.isArray(data)) {
    throw new Error('Malformed venue-account registry response');
  }
  return data as VenueAccount[];
}

export async function fetchVenueAccounts(): Promise<VenueAccount[]> {
  const { data } = await predictionManagerAxiosApi.get<VenueAccount[]>(
    '/accounts'
  );
  return parseVenueAccountsResponse(data);
}

/**
 * `enabled` gates the fetch so a dialog's registry read only happens while that
 * dialog is open, like `usePairCategories`.
 *
 * Deliberately NO empty-array fallback on error: a select with no options reads
 * as "this build has no accounts", when what actually happened is that the
 * registry was unreachable — and the operator would then conclude the group
 * cannot name an account rather than that we cannot currently ask.
 */
export function useVenueAccounts(enabled = true) {
  return useQuery<VenueAccount[]>(VENUE_ACCOUNTS_KEY, fetchVenueAccounts, {
    enabled,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false
  });
}

/**
 * The accounts offerable for selection on one venue: that platform's, ENABLED
 * only. A disabled account is refused by the start path, so offering it only
 * produces a group that 409s the first time it is started.
 */
export function selectableAccounts(
  accounts: VenueAccount[] | undefined,
  platform: VenuePlatform
): VenueAccount[] {
  return (accounts ?? []).filter(a => a.platform === platform && a.enabled);
}

/**
 * Look up one account. Scoped by platform because `accountId` is unique PER
 * PLATFORM: the same slug can name a different account on the other venue, and
 * an unscoped match would label a group with the wrong account's name.
 *
 * Returns undefined for a null id AND for an id the registry does not carry —
 * those are different states and callers must render them differently.
 */
export function findVenueAccount(
  accounts: VenueAccount[] | undefined,
  platform: VenuePlatform,
  accountId: string | null | undefined
): VenueAccount | undefined {
  if (!accountId) return undefined;
  return (accounts ?? []).find(
    a => a.platform === platform && a.accountId === accountId
  );
}
